import { getToken } from 'firebase/messaging';
import { auth, db, messaging } from './firebase';
import { doc, setDoc, collection, addDoc } from 'firebase/firestore';
import { isIOSDevice, supportsServiceWorker, isIOSWebViewGold, getWebViewGoldIOSToken } from './platform';
import { logPush } from './push-diagnostics';
import { getCachedToken } from './token-cache';

interface PushNotificationData {
  title: string;
  body: string;
  data?: Record<string, string>;
}

let notificationsInitialized = false;
let lastError: string | null = null;

async function registerIOSTokenWithRetry(
  userId: string
): Promise<boolean> {
  logPush('info', 'notification', 'Starting iOS token registration');

  try {
    const iosToken = await getWebViewGoldIOSToken();

    if (!iosToken) {
      const errorMsg = 'No iOS token available after prefetch and wait';
      logPush('error', 'notification', errorMsg);
      lastError = errorMsg;
      return false;
    }

    const cachedResult = getCachedToken();
    const tokenSource = cachedResult.source || 'unknown';

    logPush('info', 'notification', `Saving iOS token to Firestore (source: ${tokenSource})`);

    const tokenDoc = doc(db, 'users', userId, 'notificationTokens', 'current');
    await setDoc(tokenDoc, {
      token: iosToken,
      updatedAt: new Date(),
      platform: 'ios-webviewgold',
      source: tokenSource
    });

    logPush('success', 'notification', 'iOS token saved to Firestore successfully');
    lastError = null;
    return true;

  } catch (error) {
    const errorMsg = `Failed to register iOS token: ${error}`;
    logPush('error', 'notification', errorMsg, error);
    lastError = errorMsg;
    return false;
  }
}

export async function initializeNotifications() {
  if (notificationsInitialized) {
    logPush('info', 'notification', 'Already initialized - skipping');
    return;
  }

  logPush('info', 'notification', 'Starting initialization');

  try {
    if (!auth.currentUser?.uid) {
      logPush('warn', 'notification', 'User must be authenticated to initialize notifications');
      return;
    }

    logPush('info', 'notification', `User authenticated: ${auth.currentUser.uid}`);

    if (isIOSWebViewGold()) {
      logPush('info', 'notification', 'WebViewGold iOS detected - using native FCM token flow');
      try {
        const success = await registerIOSTokenWithRetry(auth.currentUser.uid);

        if (success) {
          notificationsInitialized = true;
          logPush('success', 'notification', 'iOS notification initialization completed successfully');
        } else {
          logPush('error', 'notification', 'Failed to initialize iOS notifications - token unavailable');
        }
        return;
      } catch (error) {
        const errorMsg = `Error initializing iOS WebViewGold notifications: ${error}`;
        logPush('error', 'notification', errorMsg, error);
        lastError = errorMsg;
        return;
      }
    }

    // Regular iOS devices without WebViewGold - no notification support
    if (isIOSDevice() || !supportsServiceWorker()) {
      console.log('[Notifications] iOS device detected without WebViewGold - notifications not supported');
      notificationsInitialized = true;
      return;
    }

    // Web notification flow for non-iOS devices (Android/Desktop browsers)
    console.log('[Notifications] Using standard web FCM flow (Android/Desktop browser)');

    if (!messaging) {
      console.log('[Notifications] FCM messaging not available');
      return;
    }

    if (!('Notification' in window)) {
      console.log('[Notifications] This browser does not support notifications');
      return;
    }

    let permission = Notification.permission;
    console.log('[Notifications] Current permission:', permission);

    if (permission === 'default') {
      console.log('[Notifications] Requesting notification permission...');
      permission = await Notification.requestPermission();
      console.log('[Notifications] Permission result:', permission);
    }

    if (permission === 'granted') {
      try {
        console.log('[Notifications] Getting web FCM token...');
        const token = await getToken(messaging, {
          vapidKey: 'BFk564uosqSiTH9y2UDoK7U9OjYtan1RelAa6HfPsSStRYefNqelE3kUcbG1jQKg6HNYZmh_jk8X30hZaJon1Cw'
        });

        if (token) {
          console.log('[Notifications] ✓ Web FCM token retrieved successfully');
          console.log('[Notifications] Saving token to Firestore...');

          const tokenDoc = doc(db, 'users', auth.currentUser.uid, 'notificationTokens', 'current');
          await setDoc(tokenDoc, {
            token,
            updatedAt: new Date(),
            platform: 'web'
          });

          notificationsInitialized = true;
          console.log('[Notifications] ✓ Web notification token saved to Firestore successfully');
        }
      } catch (error: any) {
        if (error.code === 'messaging/permission-blocked') {
          console.log('[Notifications] Notifications are blocked by the browser');
          return;
        }
        throw error;
      }
    } else {
      console.log('[Notifications] Permission not granted - notifications disabled');
    }
  } catch (error) {
    console.error('[Notifications] Failed to initialize notifications:', error);
    notificationsInitialized = false;
  }
}

export async function sendPushNotification({ title, body, data = {} }: PushNotificationData) {
  try {
    if (!auth.currentUser?.uid) {
      console.log('User must be authenticated to send notifications');
      return;
    }

    // Create notification document
    const notificationsRef = collection(db, 'notifications');
    const notificationData = {
      userId: auth.currentUser.uid,
      title,
      body,
      data,
      createdAt: new Date(),
      expiresAt: new Date(Date.now() + 24 * 60 * 60 * 1000), // 24h expiry
      status: 'pending',
      read: false,
      processed: false // Add processed flag
    };

    // Add to Firestore - this will trigger Cloud Functions for push notification
    await addDoc(notificationsRef, notificationData);

    if (!isIOSDevice() && process.env.NODE_ENV === 'development') {
      if (Notification.permission === 'granted') {
        new Notification(title, {
          body,
          icon: '/favicon.svg'
        });
      }
    }

  } catch (error: any) {
    if (error.code === 'permission-denied') {
      console.log('Permission denied for notifications');
      return;
    }

    if (error.message?.includes('token')) {
      notificationsInitialized = false;
      await initializeNotifications();
    }

    console.error('Error creating notification:', error);
  }
}

export function getNotificationInitStatus() {
  return {
    initialized: notificationsInitialized,
    error: lastError,
    tokenSource: getCachedToken().source
  };
}